// The HTTP client's configuration and the structural `fetch` types, on a leaf of their own (T-322, D-773): an
// app configures `createApiClient({ transport: 'http', … })` from here without the client itself — which arrives
// by dynamic import, so a demo build never carries it (`createApiClient.ts`). `HttpApiClient.ts` re-exports these.
import type { ConnectivitySource } from './connectivity';

/** The part of `fetch` this client uses, typed structurally so @sl/core needs neither the DOM nor Node typings. */
export interface FetchResponseLike {
  readonly status: number;
  readonly headers: { get(name: string): string | null };
  text(): Promise<string>;
}
export interface FetchInitLike {
  method: string;
  headers: Record<string, string>;
  body?: string;
  signal?: unknown;
}
export type FetchLike = (url: string, init: FetchInitLike) => Promise<FetchResponseLike>;

/** API §1.8: the timeout is a constructor option — 20 s on web (REQ-287 AC1 v1.2), 10 s on mobile (REQ-246 AC3). */
export const TIMEOUT_MS = { web: 20_000, mobile: 10_000 } as const;
export const DEFAULT_TIMEOUT_MS = TIMEOUT_MS.mobile;

export interface HttpApiClientConfig {
  /** Where the API lives, up to and including `/api/v1` — absolute, or relative to the page (`./api/v1`). */
  baseUrl: string;
  /** Sent as `X-Client`; the server records it as the activity-log source (API §1.1). */
  client: 'web' | 'mobile';
  /** Sent as `X-Client-Version`. */
  clientVersion: string;
  /** Default 10 s; the web app passes `TIMEOUT_MS.web`. */
  timeoutMs?: number;
  /** Default: the platform's `fetch`, looked up at call time (so a test stub installed later still applies). */
  fetch?: FetchLike;
  /** The platform's online / offline signal — `navigator.onLine` on web, NetInfo on mobile. Default: always online. */
  connectivity?: ConnectivitySource;
}
